'use client'

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Copy, Key, MoreVertical, Loader2, Plus, Eye, EyeOff } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import { Skeleton } from '../ui/skeleton'
import { apiKeysQueryOptions, API_KEYS_QUERY_KEY } from '@/lib/queries'
import { notify } from '@/lib/notify'

export default function ApiKeys() {
  const {
    isPending,
    error,
    data: apiKeys = [],
  } = useQuery({
    ...apiKeysQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const showToast = (title: string, description?: string, variant: 'default' | 'destructive' = 'default') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }

  const queryClient = useQueryClient()

  const [selectedKey, setSelectedKey] = useState<any>(null)
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false)
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isRevealDialogOpen, setIsRevealDialogOpen] = useState(false)
  const [fullApiKey, setFullApiKey] = useState('')
  const [newKeyName, setNewKeyName] = useState('')
  const [newApiKeyName, setNewApiKeyName] = useState('')

  const {
    mutate: revokeApiKey,
    isPending: isRevokingApiKey,
  } = useMutation({
    mutationFn: (id: string) =>
      api.post(`/auth/api-keys/${id}/revoke`),
    onSuccess: () => {
      setIsRevokeDialogOpen(false)
      showToast(`API key "${selectedKey?.apiKey}" has been revoked`)
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })
    },
    onError: (error: any) => {
      showToast('Error revoking API key', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    mutate: deleteApiKey,
    isPending: isDeletingApiKey,
  } = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/auth/api-keys/${id}`),
    onSuccess: () => {
      setIsDeleteDialogOpen(false)
      showToast('API key deleted')
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })
    },
    onError: (error: any) => {
      showToast('Error deleting API key', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    mutate: renameApiKey,
    isPending: isRenamingApiKey,
  } = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch(`/auth/api-keys/${id}/rename`, { name }),
    onSuccess: () => {
      setIsRenameDialogOpen(false)
      showToast(`API key renamed to "${newKeyName}"`)
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })
    },
    onError: (error: any) => {
      showToast('Error renaming API key', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    mutate: createApiKey,
    isPending: isCreatingApiKey,
  } = useMutation({
    mutationFn: (name: string) =>
      api.post('/auth/api-keys', { name }),
    onSuccess: (response) => {
      setIsCreateDialogOpen(false)
      setNewApiKeyName('')
      const apiKeyData = response.data?.data
      showToast(`API key "${apiKeyData?.apiKeyDoc?.name || 'New API key'}" created successfully`)
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })
    },
    onError: (error: any) => {
      showToast('Error creating API key', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    mutate: fetchFullApiKey,
    isPending: isFetchingFullApiKey,
  } = useMutation({
    mutationFn: (id: string) =>
      api.get(`/auth/api-keys/${id}/full`),
    onSuccess: (response) => {
      const fullKey = response.data?.data?.apiKey
      setFullApiKey(fullKey || '')
    },
    onError: (error: any) => {
      showToast('Error fetching full API key', error.response?.data?.message || error.message, 'destructive')
      setIsRevealDialogOpen(false)
    },
  })

  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey)
    showToast('API key copied to clipboard')
  }

  const handleRename = (apiKey: any) => {
    setSelectedKey(apiKey)
    setNewKeyName(apiKey.name || 'API Key')
    setIsRenameDialogOpen(true)
  }

  const handleRevoke = (apiKey: any) => {
    setSelectedKey(apiKey)
    setIsRevokeDialogOpen(true)
  }

  const handleDelete = (apiKey: any) => {
    setSelectedKey(apiKey)
    setIsDeleteDialogOpen(true)
  }

  const handleReveal = (apiKey: any) => {
    setSelectedKey(apiKey)
    setFullApiKey('')
    setIsRevealDialogOpen(true)
    fetchFullApiKey(apiKey.id)
  }

  const truncateApiKey = (apiKey: string) => {
    if (apiKey.length <= 17) return apiKey
    return apiKey.substring(0, 17) + '...'
  }

  const getDisplayName = (apiKey: any) => {
    if (apiKey.type === 'device_registration') {
      if (apiKey.device?.brand && apiKey.device?.deviceModel) {
        return `${apiKey.device.brand} ${apiKey.device.deviceModel}`
      }
      // Fallback: extract meaningful part from the generated name
      if (apiKey.name?.startsWith('Device Registration Key')) {
        return 'Device Registration Key'
      }
    }
    return apiKey.name || 'API Key'
  }

  const getAuthenticationType = (apiKey: any) => {
    return apiKey.type === 'device_registration' 
      ? 'Device Registration API Authentication'
      : 'General API Authentication'
  }

  return (
    <Card>
      <CardHeader className='pb-2'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-lg flex items-center gap-2'>
            <Key className='h-5 w-5' />
            API Keys
          </CardTitle>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className='flex items-center gap-2'
          >
            <Plus className='h-4 w-4' />
            Create API Key
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-2 max-h-96 overflow-y-auto'>
          {isPending && (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className='border-0 shadow-none'>
                  <CardContent className='flex items-center p-3'>
                    <Skeleton className='h-6 w-6 mr-3' />
                    <div className='flex-1'>
                      <div className='flex items-center justify-between'>
                        <Skeleton className='h-4 w-24' />
                        <Skeleton className='h-4 w-16' />
                      </div>
                      <div className='flex items-center space-x-2 mt-1'>
                        <Skeleton className='h-4 w-64' />
                      </div>
                      <div className='flex items-center mt-1 space-x-3'>
                        <Skeleton className='h-3 w-32' />
                        <Skeleton className='h-3 w-32' />
                      </div>
                    </div>
                    <Skeleton className='h-6 w-6' />
                  </CardContent>
                </Card>
              ))}
            </>
          )}

          {error && (
            <div className='flex justify-center items-center h-full py-8'>
              <div className='text-center'>
                <Key className='mx-auto h-12 w-12 text-destructive' />
                <h3 className='mt-2 text-sm font-medium'>Error loading API keys</h3>
                <p className='text-xs text-muted-foreground'>
                  {error.message}
                </p>
              </div>
            </div>
          )}

          {!isPending && !error && apiKeys?.length === 0 && (
            <div className='flex justify-center items-center h-full py-8'>
              <div className='text-center'>
                <Key className='mx-auto h-12 w-12 text-muted-foreground' />
                <h3 className='mt-2 text-sm font-medium'>No API keys found</h3>
                <p className='text-xs text-muted-foreground'>
                  Generate your first API key to get started
                </p>
              </div>
            </div>
          )}

          {apiKeys && Array.isArray(apiKeys) && apiKeys.map((apiKey: any) => (
            <Card key={apiKey.id} className='border-0 shadow-none hover:bg-muted/50 transition-colors'>
              <CardContent className='flex items-center p-3'>
                <Key className='h-6 w-6 mr-3 text-amber-500' />
                <div className='flex-1'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='font-semibold text-sm'>
                        {getDisplayName(apiKey)}
                      </h3>
                      <p className='text-xs text-muted-foreground'>
                        {getAuthenticationType(apiKey)}
                      </p>
                    </div>
                    <Badge
                      variant={apiKey.revokedAt ? 'secondary' : 'default'}
                      className='text-xs'
                    >
                      {apiKey.revokedAt ? 'Revoked' : 'Active'}
                    </Badge>
                  </div>
                  <div className='flex items-center space-x-2 mt-1'>
                    <code className='relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-xs flex-1'>
                      {truncateApiKey(apiKey.apiKey)}
                    </code>
                    <Button
                      variant='ghost'
                      size='icon'
                      aria-label='Reveal API key'
                      className='h-6 w-6'
                      onClick={() => handleReveal(apiKey)}
                      title='Reveal full API key'
                    >
                      <Eye className='h-3 w-3' />
                    </Button>
                  </div>
                  <div className='flex items-center mt-1 space-x-4 text-xs text-muted-foreground'>
                    <div>
                      <span className='font-medium'>Usage:</span> {apiKey.usageCount || 0} requests
                    </div>
                    <div>
                      <span className='font-medium'>Created:</span>{' '}
                      {new Date(apiKey.createdAt).toLocaleString('en-US', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </div>
                    <div>
                      <span className='font-medium'>Last used:</span>{' '}
                      {apiKey?.lastUsedAt && apiKey.usageCount > 0
                        ? new Date(apiKey.lastUsedAt).toLocaleString(
                            'en-US',
                            {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            }
                          )
                        : 'Never'}
                    </div>
                  </div>
                </div>
                <div className=''>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-label='More actions' variant='ghost' size='icon' className='h-6 w-6'>
                        <MoreVertical className='h-3 w-3' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem
                        onClick={() => handleRename(apiKey)}
                      >
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className='text-destructive'
                        onClick={() => handleRevoke(apiKey)}
                        disabled={!!apiKey.revokedAt}
                      >
                        Revoke
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className='text-destructive'
                        onClick={() => handleDelete(apiKey)}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Revoke Dialog */}
        <Dialog open={isRevokeDialogOpen} onOpenChange={setIsRevokeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke API Key</DialogTitle>
              <DialogDescription>
                Are you sure you want to revoke this API key? This action cannot
                be undone, and any applications using this key will stop working
                immediately.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsRevokeDialogOpen(false)}
                disabled={isRevokingApiKey}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => revokeApiKey(selectedKey?.id)}
                disabled={isRevokingApiKey}
              >
                {isRevokingApiKey ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : null}
                Revoke Key
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete API Key</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this API key? This action cannot
                be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeletingApiKey}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => deleteApiKey(selectedKey?.id)}
                disabled={isDeletingApiKey}
              >
                {isDeletingApiKey ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : null}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rename API Key</DialogTitle>
              <DialogDescription>
                Enter a new name for your API key.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder='Enter new name'
            />
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsRenameDialogOpen(false)}
                disabled={isRenamingApiKey}
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  renameApiKey({
                    id: selectedKey?.id,
                    name: newKeyName?.trim(),
                  })
                }
                disabled={isRenamingApiKey || !newKeyName?.trim()}
              >
                {isRenamingApiKey ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reveal API Key Dialog */}
        <Dialog open={isRevealDialogOpen} onOpenChange={(open) => {
          setIsRevealDialogOpen(open)
          if (!open) {
            setFullApiKey('')
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Full API Key</DialogTitle>
              <DialogDescription>
                Here is your complete API key. Keep it secure and do not share it publicly.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex items-center space-x-2">
                  {isFetchingFullApiKey ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading full API key...</span>
                    </div>
                  ) : (
                    <>
                      <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm flex-1 select-all break-all">
                        {fullApiKey || 'Failed to load API key'}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label='Copy API key'
                        className="h-8 w-8 shrink-0"
                        onClick={() => handleCopyApiKey(fullApiKey)}
                        title="Copy to clipboard"
                        disabled={!fullApiKey}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsRevealDialogOpen(false)}
                disabled={isFetchingFullApiKey}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create API Key Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Create a new API key to authenticate requests to the redgeGate API.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="apiKeyName">API Key Name</Label>
                <Input
                  id="apiKeyName"
                  value={newApiKeyName}
                  onChange={(e) => setNewApiKeyName(e.target.value)}
                  placeholder='Enter API key name (e.g., "My Application")'
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsCreateDialogOpen(false)
                  setNewApiKeyName('')
                }}
                disabled={isCreatingApiKey}
              >
                Cancel
              </Button>
              <Button
                onClick={() => createApiKey(newApiKeyName?.trim())}
                disabled={isCreatingApiKey || !newApiKeyName?.trim()}
              >
                {isCreatingApiKey ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                ) : null}
                Create API Key
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
